import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import apiClient from '../../api/client';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { useToast } from '../../components/ui/Toast';
import {
  cmdHeaders,
  returnError,
  CONDITIONS,
  condLabel,
  Field,
} from '../../components/returns/returnsShared';

/**
 * Return without a receipt — only when the purchase cannot be found (searched by customer, date and
 * reference). A private customer only; a manager decides; settled by purchase voucher at today's
 * price.
 */
interface Product {
  id: string;
  sku: string;
  name: string;
  brand: string | null;
}
interface Customer {
  id: string;
  name: string;
  customer_type: string;
}

export function NoReceiptDialog({
  onClose,
  onDone,
}: {
  onClose: () => void;
  onDone: (id: string) => void;
}) {
  const { t } = useTranslation();
  const { push } = useToast();
  const [pq, setPq] = useState('');
  const [products, setProducts] = useState<Product[]>([]);
  const [product, setProduct] = useState<Product | null>(null);
  const [cq, setCq] = useState('');
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [qty, setQty] = useState('1');
  const [condition, setCondition] = useState('INTACT');
  const [note, setNote] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (product || pq.trim().length < 2) {
      setProducts([]);
      return;
    }
    const h = setTimeout(
      () =>
        apiClient
          .get<Product[]>(`/returns/products?q=${encodeURIComponent(pq.trim())}`)
          .then((r) => setProducts(r.data))
          .catch(() => setProducts([])),
      200
    );
    return () => clearTimeout(h);
  }, [pq, product]);
  useEffect(() => {
    if (customer || cq.trim().length < 2) {
      setCustomers([]);
      return;
    }
    const h = setTimeout(
      () =>
        apiClient
          .get<{ rows: Customer[] }>(`/customers?q=${encodeURIComponent(cq.trim())}&limit=8`)
          .then((r) => setCustomers(r.data.rows.filter((c) => c.customer_type !== 'B2B')))
          .catch(() => setCustomers([])),
      200
    );
    return () => clearTimeout(h);
  }, [cq, customer]);

  const save = async () => {
    if (!product || !customer) {
      setErr(t('returnsV2.noReceipt.missing'));
      return;
    }
    setBusy(true);
    setErr('');
    try {
      const { data } = await apiClient.post<{ id: string; returnNumber: string }>(
        '/returns/no-receipt',
        {
          customerId: customer.id,
          productId: product.id,
          qty: Number(qty),
          condition,
          note,
        },
        cmdHeaders()
      );
      push({
        tone: 'success',
        message: t('returnsV2.noReceipt.done', { number: data.returnNumber }),
      });
      onDone(data.id);
    } catch (e) {
      setErr(returnError(t, e));
    } finally {
      setBusy(false);
    }
  };

  const pickList = <T extends { id: string }>(
    items: T[],
    label: (x: T) => React.ReactNode,
    onPick: (x: T) => void
  ) =>
    items.length > 0 && (
      <div className="absolute z-30 mt-1 max-h-56 w-full overflow-auto rounded-md border bg-popover shadow-lg">
        {items.map((x) => (
          <button
            key={x.id}
            type="button"
            className="block w-full px-3 py-2 text-left text-sm hover:bg-accent"
            onClick={() => onPick(x)}
          >
            {label(x)}
          </button>
        ))}
      </div>
    );

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        className="max-h-[92vh] w-full max-w-2xl overflow-auto rounded-lg border bg-background shadow-xl"
      >
        <header className="border-b px-5 py-4">
          <h3 className="text-base font-semibold">{t('returnsV2.noReceipt.title')}</h3>
          <p className="mt-1 text-sm text-muted-foreground">{t('returnsV2.noReceipt.lede')}</p>
        </header>
        <div className="grid gap-3 px-5 py-4 sm:grid-cols-2">
          <Field label={t('returnsV2.noReceipt.part')} className="relative sm:col-span-2">
            {product ? (
              <div className="flex h-9 items-center justify-between rounded-md border px-3 text-sm">
                <span>
                  <b className="font-mono">{product.sku}</b> {product.name}
                </span>
                <button
                  type="button"
                  className="text-xs text-primary hover:underline"
                  onClick={() => {
                    setProduct(null);
                    setPq('');
                  }}
                >
                  {t('returnsV2.new.change')}
                </button>
              </div>
            ) : (
              <>
                <Input
                  value={pq}
                  onChange={(e) => setPq(e.target.value)}
                  placeholder={t('returnsV2.noReceipt.partHint')}
                  autoFocus
                />
                {pickList(
                  products,
                  (p) => (
                    <>
                      <b className="font-mono">{p.sku}</b> {p.brand} · {p.name}
                    </>
                  ),
                  (p) => setProduct(p)
                )}
              </>
            )}
          </Field>
          <Field label={t('returnsV2.noReceipt.customer')} className="relative sm:col-span-2">
            {customer ? (
              <div className="flex h-9 items-center justify-between rounded-md border px-3 text-sm">
                <b>{customer.name}</b>
                <button
                  type="button"
                  className="text-xs text-primary hover:underline"
                  onClick={() => {
                    setCustomer(null);
                    setCq('');
                  }}
                >
                  {t('returnsV2.new.change')}
                </button>
              </div>
            ) : (
              <>
                <Input
                  value={cq}
                  onChange={(e) => setCq(e.target.value)}
                  placeholder={t('returnsV2.noReceipt.customerHint')}
                />
                {pickList(
                  customers,
                  (c) => (
                    <b>{c.name}</b>
                  ),
                  (c) => setCustomer(c)
                )}
              </>
            )}
          </Field>
          <Field label={t('returnsV2.new.qty')}>
            <Input type="number" min={1} value={qty} onChange={(e) => setQty(e.target.value)} />
          </Field>
          <Field label={t('returnsV2.new.condition')}>
            <Select value={condition} onChange={(e) => setCondition(e.target.value)}>
              {CONDITIONS.map((k) => (
                <option key={k} value={k}>
                  {condLabel(t, k)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t('returnsV2.noReceipt.note')} className="sm:col-span-2">
            <Input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t('returnsV2.noReceipt.noteHint')}
            />
          </Field>
        </div>
        <footer className="flex flex-wrap items-center justify-end gap-2 border-t px-5 py-3">
          {err && (
            <p className="flex-1 text-sm font-medium text-destructive" role="alert">
              {err}
            </p>
          )}
          <Button variant="outline" onClick={onClose}>
            {t('returnsV2.cancel')}
          </Button>
          <Button disabled={busy} onClick={() => void save()}>
            {t('returnsV2.noReceipt.save')}
          </Button>
        </footer>
      </div>
    </div>
  );
}
